import { useCallback } from 'react';
import Store from 'store';
import Api from '../../../service/api';

/**
 * One-shot API calls: token, profile, responsavel, communication data,
 * session status, assignment and message reading.
 */
const useChatApi = ({
    apiCHAT,
    tokenChat,
    setTokenChat,
    profile,
    selectedSessionId,
    selectedSession,
    setSelectedSession,
    isMountedRef,
    broadcastSessionUpdate,
    updateSessionInList,
    fetchChatSessions,
    currentPage,
    searchQuery,
    searchStatus,
    setChatResponsavel,
    setChatProfile,
}) => {
    // ─── URL helper (needs access to apiCHAT) ────────────────────────────────

    const urlChat = useCallback((path) => `${apiCHAT.defaults.baseURL}${path}`, [apiCHAT]);

    // ─── Token ────────────────────────────────────────────────────────────────

    const gerarTokenChat = useCallback(async () => {
        const url = urlChat('token/api');
        const dataLogin = Store.get('logins') ? JSON.parse(Store.get('logins')) : {};

        const tokenAltum = Store.get('token_altum');

        const userName = dataLogin.login || String(profile.pess_usuario) || 'unknown_user';

        try {
            const response = await apiCHAT.post(
                url,
                {
                    username: userName, // Garantindo que seja uma string, mesmo que login seja undefined
                    token: tokenAltum, // Usando token_altum como senha para autenticação
                },
                { headers: { 'Content-Type': 'application/json' } },
            );

            const chatToken = response.data.access_token;
            const idUsuario = response.data.id_usuario;

            if (isMountedRef.current) {
                Store.set('id_usuario', idUsuario);
                Store.set('token_chat', chatToken);
                setTokenChat(chatToken);
            }
            return chatToken;
        } catch (error) {
            console.error('Erro ao gerar token:', error);
            return null;
        }
    }, [apiCHAT, urlChat, isMountedRef, setTokenChat]);

    // ─── Profile ──────────────────────────────────────────────────────────────

    const obterProfile = useCallback(async () => {
        let token = tokenChat;

        if (!token) {
            token = await gerarTokenChat();
            if (!isMountedRef.current) return;
            setTokenChat(token);
            if (!token) return;
        }

        try {
            const response = await apiCHAT.get('/chat_perfil/show', {
                params: { id_usuario: profile.pess_id_pessoa },
                headers: { Authorization: `Bearer ${token}` },
            });

            if (isMountedRef.current) setChatProfile(response.data);
        } catch (error) {
            console.error('Erro ao buscar perfil:', error);
        }
    }, [apiCHAT, tokenChat, profile, gerarTokenChat, isMountedRef, setTokenChat, setChatProfile]);

    // ─── Responsável ──────────────────────────────────────────────────────────

    const obterResponsavel = useCallback(
        async (sessionId, token) => {
            if (!isMountedRef.current) return null;

            const url = urlChat('chat_responsavel/show');

            try {
                const response = await apiCHAT.get(url, {
                    params: { id_sessao_chat: sessionId },
                    headers: { Authorization: `Bearer ${token}` },
                    body: {},
                });

                if (isMountedRef.current) setChatResponsavel(response.data);
                return response.data;
            } catch (error) {
                console.error('Erro ao obter responsável:', error);
                return null;
            }
        },
        [apiCHAT, urlChat, isMountedRef, setChatResponsavel],
    );

    // ─── Communication data ───────────────────────────────────────────────────

    const fetchAreasForCommunication = useCallback(async (name = null) => {
        try {
            const resp = await Api.get('client/settings/areas', { params: { name } });
            if (resp.data.areas.length > 0) {
                return resp.data.areas.map((item) => ({
                    key: `comunicacao_interna_interacao_area_${item.name
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')
                        .replace(/ç/gi, 'c')
                        .replace(/[^a-z0-9\s]/gi, '')
                        .replace(/\s+/g, '_')
                        .toLowerCase()}`,
                    label: item.name,
                    id: item.id,
                    attachment_is_required: item.attachment_is_required,
                    is_active: item.is_active,
                }));
            }
            return [];
        } catch (e) {
            console.error('Error fetching areas:', e);
            return [];
        }
    }, []);

    const fetchTiposForCommunication = useCallback(async (areaName = null) => {
        try {
            const resp = await Api.get('client/settings/categories', { params: { area_name: areaName } });
            if (resp.data.categories?.length > 0) {
                return resp.data.categories.map((item) => ({
                    key:
                        `comunicacao_interna_interacao_${item.name
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, '')
                            .replace(/ç/gi, 'c')
                            .replace(/[^a-z0-9\s]/gi, '')
                            .replace(/\s+/g, '_')
                            .toLowerCase()}` + `_${item.id}`,
                    label: item.name,
                    id: item.id,
                    attachment_is_required: item.area.attachment_is_required,
                    is_active: item.area.is_active,
                }));
            }
            return [];
        } catch (e) {
            console.error('Error fetching tipos:', e);
            return [];
        }
    }, []);

    const fetchCommunicationBySession = useCallback(
        async (sessionId) => {
            if (!tokenChat) return null;

            try {
                const response = await apiCHAT.get(`comunicacao_interna_altum/session/${sessionId}`, {
                    headers: { Authorization: `Bearer ${tokenChat}` },
                });
                return response.data;
            } catch (error) {
                console.error('Erro ao buscar comunicação interna por sessão', error);
                return null;
            }
        },
        [apiCHAT, tokenChat],
    );

    const fetchRegionalCommunication = useCallback(async () => {
        try {
            const resp = await Api.get('client/settings/locations');
            if (resp.data.locations.length > 0) {
                return resp.data.locations.map((item, index) => ({
                    key: `comunicacao_interna_interacao_salvar_regional_${item.id || index}`,
                    label: item.name,
                }));
            }
            return [];
        } catch (e) {
            console.error('Error fetching locations:', e);
            return [];
        }
    }, []);

    // ─── Session status ───────────────────────────────────────────────────────

    const mudarStatusSessao = useCallback(
        async (status) => {
            const url = urlChat('chat_sessions/status');
            if (!tokenChat) return;

            try {
                await apiCHAT.put(
                    url,
                    { id: selectedSessionId, status },
                    { headers: { Authorization: `Bearer ${tokenChat}` } },
                );

                broadcastSessionUpdate(selectedSessionId, status);
                updateSessionInList(selectedSessionId, status);

                if (selectedSession?.id === selectedSessionId) {
                    setSelectedSession((prev) => ({ ...prev, status }));
                }

                await fetchChatSessions(currentPage, tokenChat, searchQuery, searchStatus);
                return true;
            } catch (error) {
                console.error('Erro ao mudar o status', error);
                return false;
            }
        },
        [
            apiCHAT,
            tokenChat,
            urlChat,
            selectedSessionId,
            selectedSession,
            setSelectedSession,
            broadcastSessionUpdate,
            updateSessionInList,
            fetchChatSessions,
            currentPage,
            searchQuery,
            searchStatus,
        ],
    );

    // ─── Session assignment ───────────────────────────────────────────────────

    const atribuirSessaoChat = useCallback(
        async (status) => {
            const url = urlChat('chat_responsavel/create');
            if (!tokenChat) return;

            try {
                await apiCHAT.post(
                    url,
                    { id_sessao_chat: selectedSessionId, status },
                    { headers: { Authorization: `Bearer ${tokenChat}` } },
                );
                return true;
            } catch (error) {
                console.error('Erro atribuir a sessão', error);
                return false;
            }
        },
        [apiCHAT, tokenChat, urlChat, selectedSessionId],
    );

    // ─── Link communication ───────────────────────────────────────────────────

    const handleLinkCommunication = useCallback(
        async (idComunicacaoInterna) => {
            if (!tokenChat || !selectedSessionId) return false;

            try {
                await apiCHAT.post(
                    'chatbot_comunicacao_interna_altum',
                    { id_sessao_chat: selectedSessionId, id_comunicacao_interna: idComunicacaoInterna },
                    { headers: { Authorization: `Bearer ${tokenChat}` } },
                );
                return true;
            } catch (error) {
                console.error('Erro ao vincular comunicação interna', error);
                return false;
            }
        },
        [apiCHAT, tokenChat, selectedSessionId],
    );

    // ─── Read message ─────────────────────────────────────────────────────────

    const lerMensagem = useCallback(
        async (messageId, sessionId) => {
            if (!tokenChat || !isMountedRef.current || !messageId || !sessionId) return;

            try {
                await apiCHAT.post(
                    '/chat_mensagens/ler',
                    {
                        id_usuario: profile.pess_id_pessoa,
                        id_sessao_chat: sessionId,
                        chatbot_mensagem_id: messageId,
                        lido: 1,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${tokenChat}`,
                            'Content-Type': 'application/json',
                        },
                    },
                );
            } catch (error) {
                console.error('Erro ao marcar mensagem como lida:', error);
            }
        },
        [apiCHAT, tokenChat, profile.pess_id_pessoa, isMountedRef],
    );

    return {
        urlChat,
        gerarTokenChat,
        obterProfile,
        obterResponsavel,
        fetchAreasForCommunication,
        fetchTiposForCommunication,
        fetchCommunicationBySession,
        fetchRegionalCommunication,
        mudarStatusSessao,
        atribuirSessaoChat,
        handleLinkCommunication,
        lerMensagem,
    };
};

export default useChatApi;
